import * as React from 'react';

import {
  Badge,
  Col,
  Popover,
  PopoverDialog,
  Row,
  UnstyledButton,
  withColor,
} from '@rematter/ui-web';
import type { ThemeColor } from '@rematter/ui-web';
import { format } from 'date-fns';
import {
  LucideBuilding2,
  LucideCalendarDays,
  LucideExternalLink,
  LucideTag,
} from 'lucide-react';

import { useTranslations } from '../i18n/useTranslations';
import type {
  CalendarEvent,
  CalendarEventSource,
} from '../types/calendarEvent';
import {
  DEFAULT_MODULE_COLORS,
  DEFAULT_STATUS_COLORS,
} from '../utils/colorDefaults';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

interface CalendarEventPopoverProps {
  event: CalendarEvent | null;
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
}

function formatEventDate(event: CalendarEvent): string {
  const startDate = new Date(event.start);
  if (event.allDay) {
    const formatted = format(startDate, 'MMM d, yyyy');
    if (event.end) {
      const endDate = new Date(event.end);
      return `${formatted} - ${format(endDate, 'MMM d, yyyy')}`;
    }
    return formatted;
  }
  const formatted = format(startDate, 'MMM d, yyyy h:mm a');
  if (event.end) {
    const endDate = new Date(event.end);
    return `${formatted} - ${format(endDate, 'h:mm a')}`;
  }
  return formatted;
}

function getStatusColor(status: string): ThemeColor {
  const normalized = status.toLowerCase();
  return DEFAULT_STATUS_COLORS[normalized] ?? 'neutral';
}

export const CalendarEventPopover = ({
  event,
  anchorEl,
  isOpen,
  onClose,
}: CalendarEventPopoverProps) => {
  const t = useTranslations();
  const [, setLocation] = useModuleLocation();
  const triggerRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    triggerRef.current = anchorEl;
  }, [anchorEl]);

  if (!event || !anchorEl) return null;

  const moduleColor = DEFAULT_MODULE_COLORS[event.module];
  const sourceLabel = t.sources[event.source as CalendarEventSource];

  return (
    <Popover
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      triggerRef={triggerRef}
      placement="bottom start"
      className="@CalendarEventPopover w-72"
    >
      <PopoverDialog>
        <Col className="p-3" gap3>
          <Row yCenter gap2>
            <Badge color={moduleColor} size="small">
              {t.sidebar.modules[event.module]}
            </Badge>
          </Row>

          <span className="text-sm font-semibold text-neutral">
            {event.title}
          </span>

          <Col gap2>
            <Row yCenter gap2 className="text-xs text-neutral/60">
              <LucideCalendarDays className="size-3.5 shrink-0" />
              <span>{formatEventDate(event)}</span>
            </Row>

            {event.status && (
              <Row yCenter gap2 className="text-xs text-neutral/60">
                <LucideTag className="size-3.5 shrink-0" />
                <span>{t.popover.status}:</span>
                <span
                  className="font-medium text-custom-color"
                  style={withColor(getStatusColor(event.status))}
                >
                  {event.status}
                </span>
              </Row>
            )}

            {event.facilityName && (
              <Row yCenter gap2 className="text-xs text-neutral/60">
                <LucideBuilding2 className="size-3.5 shrink-0" />
                <span>{event.facilityName}</span>
              </Row>
            )}

            <Row yCenter gap2 className="text-xs text-neutral/60">
              <LucideExternalLink className="size-3.5 shrink-0" />
              <span>
                {t.popover.source}: {sourceLabel}
              </span>
            </Row>
          </Col>

          <UnstyledButton
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:text-primary-darker"
            onPress={() => {
              setLocation(event.entityUrl);
              onClose();
            }}
          >
            {t.popover.viewDetails}
            <LucideExternalLink className="size-3" />
          </UnstyledButton>
        </Col>
      </PopoverDialog>
    </Popover>
  );
};
